import moment from 'moment';
import { authUtilizationPeriodEnums as PERIOD, authUtilizationStateEnums as STATE } from 'utils';
import { frequencyLimitModeEnums } from 'fragments/client/clientSingle/core/constants';
import { TERM_PACE_MIN_DAYS } from './constants';

/**
 * Turns the `GET /authorizedService/utilization` page — `[{ client, authorizations: AuthorizationDTO[] }]`,
 * each service carrying `status` and `periods` — into the rows the timeline renders.
 *
 * One row per authorized service (a service code under an auth number).
 *
 * Statuses come from the API: a service's `status` is term pace, a period's `status` is booked vs. the
 * weekly/monthly limit, and `filterStatus` is the row's one Status-filter value. This file only adds display state —
 * percentages, where a period sits relative to today, gaps, projection.
 * Weekly/monthly services render one chip per period; entire-term services render one bar.
 */

/** A function, not a module-level map: `utils` and `store` import each other, so enums can be undefined at load. */
const periodOf = (mode) => {
    if (mode === frequencyLimitModeEnums.WEEKLY) return PERIOD.WEEK;
    if (mode === frequencyLimitModeEnums.MONTHLY) return PERIOD.MONTH;
    return PERIOD.TERM;
};

const DATE = 'YYYY-MM-DD';
const day = (date) => moment(date).startOf('day');

const stateOf = (start, end, today) => {
    if (end.isBefore(today, 'day')) return STATE.COMPLETED;
    if (start.isAfter(today, 'day')) return STATE.SCHEDULED;
    return STATE.CURRENT;
};

const percent = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : null);

/** `utilization` is null (an "available" chip) when nothing is booked and the API gave no status. */
const toPeriod = ({ start, end, authorizedUnits, completedUnits = 0, scheduledUnits = 0, status = null }, today) => {
    const booked = completedUnits + scheduledUnits;
    return {
        startDate: start.format(DATE),
        endDate: end.format(DATE),
        state: stateOf(start, end, today),
        authorizedUnits,
        completedUnits,
        scheduledUnits,
        utilization: authorizedUnits && (booked || status) ? percent(booked, authorizedUnits) : null,
        status,
    };
};

/** The API's weeks (WEEKLY, ENTIRE_TERM) or months (MONTHLY), already clipped to the visible range. */
const apiPeriods = (service, today) =>
    (service?.periods || []).map((p) => toPeriod({ ...p, start: day(p.startDate), end: day(p.endDate) }, today));

/** Entire-term services draw one bar for the whole pool. */
const termPeriod = (service, today) =>
    toPeriod(
        {
            start: day(service.startDate),
            end: day(service.endDate),
            authorizedUnits: service?.totalUnits || 0,
            completedUnits: service?.completedUnits || 0,
            scheduledUnits: service?.reservedUnits || 0,
            status: service?.status ?? null,
        },
        today
    );

const overlapsRange = (row, range) => !day(row.startDate).isAfter(range.end) && !day(row.endDate).isBefore(range.start);

/** Days with no coverage between the previous auth for the same service code and this one. */
const attachGaps = (rows) => {
    const lastEndByCode = {};
    [...rows]
        .sort((a, b) => (a.startDate < b.startDate ? -1 : 1))
        .forEach((row) => {
            if (row.cancelled) return; // a cancelled auth never covered anything
            const prevEnd = lastEndByCode[row.serviceCode];
            if (prevEnd) {
                const gapStart = prevEnd.clone().add(1, 'day');
                const gapEnd = day(row.startDate).subtract(1, 'day');
                if (!gapEnd.isBefore(gapStart)) {
                    row.gapBefore = {
                        startDate: gapStart.format(DATE),
                        endDate: gapEnd.format(DATE),
                        days: gapEnd.diff(gapStart, 'days') + 1,
                    };
                }
            }
            const end = day(row.endDate);
            if (!prevEnd || end.isAfter(prevEnd)) lastEndByCode[row.serviceCode] = end;
        });
    return rows;
};

/**
 * Straight-line projection from the completed-units burn rate so far.
 * Ahead: the pool runs out before the auth ends. Behind: share of the pool used by the end at this rate.
 */
const projectionOf = ({ start, end, authorizedUnits, completedUnits }, today) => {
    if (!authorizedUnits || today.isBefore(start) || today.isAfter(end)) return null;
    const elapsedDays = today.diff(start, 'days') + 1;
    const totalDays = end.diff(start, 'days') + 1;
    if (elapsedDays < Math.min(TERM_PACE_MIN_DAYS, totalDays)) return null;
    const perDay = completedUnits / elapsedDays;
    if (!perDay) return null;

    const runOut = start.clone().add(Math.ceil(authorizedUnits / perDay) - 1, 'days');
    if (runOut.isBefore(end)) return { ahead: true, runOutDate: runOut.format(DATE), daysEarly: end.diff(runOut, 'days') };
    return { ahead: false, projectedPercent: percent(perDay * totalDays, authorizedUnits) };
};

const toRow = (authorization, service, today) => {
    const period = periodOf(service?.frequencyLimit?.mode);
    const weeksOrMonths = apiPeriods(service, today);
    const cancelled = service?.active === false;
    const start = day(service.startDate);
    const end = day(service.endDate);
    const completedUnits = service?.completedUnits || 0;
    const scheduledUnits = service?.reservedUnits || 0;
    return {
        id: service.id,
        groupId: `${authorization.payerId}_${authorization.authorizationUniqueId}`,
        unitSize: service?.service?.unitSize || null,
        overall: {
            authorizedUnits: service.totalUnits,
            completedUnits,
            scheduledUnits,
            usedUnits: completedUnits + scheduledUnits,
            remainingUnits: Math.max(0, service.totalUnits - completedUnits - scheduledUnits),
            utilization: percent(completedUnits + scheduledUnits, service.totalUnits) ?? 0,
        },
        projection: projectionOf({ start, end, authorizedUnits: service.totalUnits, completedUnits }, today),
        authId: service.authorizationId || authorization.authorizationId,
        serviceCode: service?.service?.cptCode || service?.service?.name || '',
        payer: service.payerName || authorization.payerName,
        staffName: service.renderingProviderName || authorization.renderingProviderName,
        period,
        unitsPerPeriod: service?.frequencyLimit?.limit ?? null,
        totalUnits: service.totalUnits,
        startDate: day(service.startDate).format(DATE),
        endDate: day(service.endDate).format(DATE),
        status: service?.status ?? null,
        /** The row's one Status-filter value from the API — filtering only, never colors (those use `status`). */
        filterStatus: service?.filterStatus ?? null,
        cancelled,
        cancelledReason: cancelled ? service?.deactivatedReason || null : null,
        expired: !cancelled && end.isBefore(today, 'day'),
        hasPeriodBreakdown: period !== PERIOD.TERM,
        gapBefore: null,
        periods: period === PERIOD.TERM ? [termPeriod(service, today)] : weeksOrMonths,
        /** Booked units before the visible range — the weeks the overall totals count but the chips don't show. */
        earlierUnits: service?.earlierUnits || 0,
    };
};

/** No-auth-needed and zero-unit services aren't metered. Cancelled ones stay — the timeline marks them as cancelled. */
const isMetered = (service) => !service?.noAuthNeeded && service?.totalUnits > 0;

const byCodeThenStart = (a, b) =>
    a.serviceCode === b.serviceCode ? (a.startDate < b.startDate ? -1 : 1) : a.serviceCode < b.serviceCode ? -1 : 1;

/**
 * Output: `[{ id, firstName, lastName, services: [row] }]` — one row per authorized service directly under the
 * client, as in Figma; the auth # and provider are on each row's sub-line. Rows of the same authorization stay
 * together (authorizations oldest first, then service code). `statuses` (filter-status values, empty = all) hides
 * rows the API returned only so gaps stay correct.
 */
export const buildUtilizationClients = (entries, range, statuses = []) => {
    const today = moment().startOf('day');

    return (entries || [])
        .map(({ client, authorizations }) => {
            const authOrder = [...(authorizations || [])]
                .sort((a, b) => (a.startDate < b.startDate ? -1 : 1))
                .map((authorization) => `${authorization.payerId}_${authorization.authorizationUniqueId}`);
            const rows = (authorizations || []).flatMap((authorization) =>
                (authorization?.authorizedServices || []).filter(isMetered).map((service) => toRow(authorization, service, today))
            );
            // Gaps are computed before filtering so a hidden predecessor still marks the gap.
            const services = attachGaps(rows)
                .filter((row) => overlapsRange(row, range))
                .filter((row) => !statuses.length || statuses.includes(row.filterStatus))
                .sort((a, b) => authOrder.indexOf(a.groupId) - authOrder.indexOf(b.groupId) || byCodeThenStart(a, b));

            return { ...client, services };
        })
        .filter((client) => client.services.length);
};
